import { useState } from "react";
import { debates } from "@/mocks/opinion";

export default function DebateSection() {
  const [activeFor, setActiveFor] = useState<Record<string, boolean>>({ db1: true, db2: true, db3: true });

  const pick = (id: string) => !(activeFor[id] ?? true);
  const select = (id: string, isFor: boolean) => setActiveFor((p) => ({ ...p, [id]: isFor }));

  return (
    <section id="debate" className="bg-background-50">
      <div className="mx-auto max-w-6xl px-4 md:px-6 py-12 md:py-16">
        <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 text-xs md:text-sm font-semibold uppercase tracking-widest text-primary-600">
              <span className="h-px w-6 bg-primary-500"></span> Both Sides
            </p>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl font-black text-foreground-950">争论焦点</h2>
            <p className="mt-3 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-700">
              同一件事，评论区往往有两种都讲得通的逻辑。切到不同立场，看看大家都在争什么。
            </p>
          </div>
          <p className="inline-flex items-center gap-1.5 text-xs text-foreground-600">
            <i className="ri-arrow-left-right-line text-base" aria-hidden="true"></i>
            点击阵营标签切换观点
          </p>
        </div>

        <div className="mt-8 space-y-5">
          {debates.map((d) => {
            const isFor = activeFor[d.id] ?? true;
            const active = isFor ? d.forLabel : d.againstLabel;
            const passive = isFor ? d.againstLabel : d.forLabel;
            const activeText = isFor ? d.forText : d.againstText;
            const activeComments = isFor ? d.forComments : d.againstComments;
            const toggle = () => select(d.id, pick(d.id));

            return (
              <article
                key={d.id}
                className="overflow-hidden rounded-2xl border border-background-200 bg-background-100/60"
              >
                <div className="flex flex-col gap-3 border-b border-background-200 bg-background-50 px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6">
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className="ri-question-mark" aria-hidden="true"></i>
                    </span>
                    <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950">{d.topic}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1 self-start text-xs text-foreground-600 md:self-auto">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {d.readTime}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* active side */}
                  <div className="border-b border-background-200 p-5 md:border-b-0 md:border-r md:p-6 bg-background-50/70">
                    <button
                      type="button"
                      onClick={toggle}
                      aria-pressed={isFor}
                      className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors cursor-pointer ${
                        isFor ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700 hover:bg-secondary-200"
                      }`}
                    >
                      <i className={`${isFor ? "ri-thumb-up-line" : "ri-thumb-down-line"} text-base`} aria-hidden="true"></i>
                      {active}
                    </button>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-800">{activeText}</p>
                    <ul className="mt-4 space-y-2 border-t border-background-200 pt-3">
                      {activeComments.map((c, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                          <span className={`mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full ${isFor ? "bg-primary-500" : "bg-accent-500"}`}></span>
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* passive side preview */}
                  <div className="flex flex-col p-5 md:p-6">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        onClick={toggle}
                        aria-pressed={!isFor}
                        className={`inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-semibold transition-colors cursor-pointer ${
                          !isFor ? "bg-accent-500 text-background-50" : "bg-background-200 text-foreground-700 hover:bg-secondary-200"
                        }`}
                      >
                        <i className={`${!isFor ? "ri-thumb-up-line" : "ri-thumb-down-line"} text-base`} aria-hidden="true"></i>
                        {passive}
                      </button>
                      {!isFor && (
                        <span className="text-xs text-accent-700">正在展示该阵营</span>
                      )}
                    </div>
                    {isFor ? (
                      <div className="mt-3 flex flex-1 flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/40 p-5 text-center">
                        <i className="ri-eye-off-line text-2xl text-foreground-400" aria-hidden="true"></i>
                        <p className="mt-2 text-xs text-foreground-600">切换标签查看对立阵营的观点与热评</p>
                        <button
                          type="button"
                          onClick={toggle}
                          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent-700 hover:text-accent-800 cursor-pointer"
                        >
                          看看另一边怎么说 <i className="ri-arrow-right-line" aria-hidden="true"></i>
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="mt-3 text-sm leading-relaxed text-foreground-800">{activeText}</p>
                        <ul className="mt-4 space-y-2 border-t border-background-200 pt-3">
                          {activeComments.map((c, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-foreground-700">
                              <span className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-500"></span>
                              <span>{c}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}